import { Dialog as D } from 'radix-ui';
import { X } from 'lucide-react';
import * as React from 'react';
import { t } from '@wally-reseller/shared';
import { cn } from '../utils';
import { useUiLocale } from './locale';

export const Dialog = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;

export function DialogContent({
  className,
  children,
  title,
  description,
  ...props
}: React.ComponentProps<typeof D.Content> & { title: string; description?: string }) {
  const locale = useUiLocale();
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]" />
      <D.Content
        className={cn(
          'fixed left-1/2 top-1/2 z-50 w-[calc(100%-32px)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-popover p-5 shadow-xl focus:outline-none',
          className,
        )}
        {...props}
      >
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <D.Title className="text-base font-semibold">{title}</D.Title>
            {description ? (
              <D.Description className="mt-0.5 text-sm text-muted-foreground">
                {description}
              </D.Description>
            ) : (
              <D.Description className="sr-only">{title}</D.Description>
            )}
          </div>
          <D.Close
            className="rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label={t(locale, 'ui.close')}
          >
            <X className="size-4" />
          </D.Close>
        </div>
        {children}
      </D.Content>
    </D.Portal>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  danger,
  onConfirm,
  busy,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  busy?: boolean;
}) {
  const locale = useUiLocale();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={title} description={description}>
        <div className="mt-2 flex justify-end gap-2">
          <D.Close className="h-8 rounded-md border border-border px-3 text-sm hover:bg-muted">
            {t(locale, 'ui.cancel')}
          </D.Close>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className={cn(
              'h-8 rounded-md px-3 text-sm font-medium text-white disabled:opacity-50',
              danger ? 'bg-danger' : 'bg-accent',
            )}
          >
            {confirmLabel ?? t(locale, 'ui.confirm')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
